import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { pagesAPI } from '../utils/api';
import { Plus, LogOut, Clock, AlertCircle, CheckCircle, XCircle, CheckCircle2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const NurseDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchPages();
    const interval = setInterval(fetchPages, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  const fetchPages = async () => {
    try {
      const response = await pagesAPI.getAll();
      setPages(response.data);
    } catch (error) {
      console.error('Error fetching pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      1: 'bg-red-100 text-red-800 border-red-300',
      2: 'bg-blue-100 text-blue-800 border-blue-300',
      3: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      4: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return colors[priority] || colors[4];
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      1: 'URGENT',
      2: 'SCHEDULE',
      3: 'DELEGATE',
      4: 'DEFER'
    };
    return labels[priority] || 'UNKNOWN';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-blue-600" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-gray-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-orange-600" />;
    }
  };

  const filteredPages = pages.filter(page => {
    if (filter === 'all') return true;
    return page.status === filter;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-2 sm:gap-4">
              <img src="/REACH_LOGO.png" alt="Reach Logo" className="h-10 sm:h-12 w-auto bg-transparent" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Nurse Dashboard</h1>
                <p className="text-xs sm:text-sm text-gray-600">Welcome, {user?.name}</p>
              </div>
            </div>
            <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
              <button
                onClick={() => navigate('/create-page')}
                className="flex items-center gap-1 sm:gap-2 bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base flex-1 sm:flex-initial justify-center"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Create Message</span>
                <span className="sm:hidden">Create</span>
              </button>
              <button
                onClick={logout}
                className="flex items-center gap-1 sm:gap-2 bg-gray-200 text-gray-700 px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors text-sm sm:text-base flex-1 sm:flex-initial justify-center"
              >
                <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Logout</span>
                <span className="sm:hidden">Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow">
            <p className="text-xs sm:text-sm text-gray-600">Total Pages</p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">{pages.length}</p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow">
            <p className="text-xs sm:text-sm text-gray-600">Pending</p>
            <p className="text-xl sm:text-2xl font-bold text-orange-600">
              {pages.filter(p => p.status === 'pending').length}
            </p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow">
            <p className="text-xs sm:text-sm text-gray-600">In Progress</p>
            <p className="text-xl sm:text-2xl font-bold text-blue-600">
              {pages.filter(p => p.status === 'in_progress').length}
            </p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow">
            <p className="text-xs sm:text-sm text-gray-600">Completed</p>
            <p className="text-xl sm:text-2xl font-bold text-green-600">
              {pages.filter(p => p.status === 'completed').length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex flex-wrap gap-2">
            {['all', 'pending', 'acknowledged', 'in_progress', 'completed'].map(status => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base flex-1 sm:flex-initial min-w-[80px] ${
                  filter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Pages List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading pages...</p>
            </div>
          ) : filteredPages.length === 0 ? (
            <div className="bg-white p-12 rounded-lg shadow text-center">
              <p className="text-gray-600">No pages found</p>
              <button
                onClick={() => navigate('/create-page')}
                className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
              >
                Create your first page
              </button>
            </div>
          ) : (
            filteredPages.map(page => (
              <div
                key={page.id}
                onClick={() => navigate(`/page/${page.id}`)}
                className="bg-white p-4 sm:p-6 rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer"
              >
                {/* Header Row */}
                <div className="flex items-start gap-2 sm:gap-3 mb-3">
                  {getStatusIcon(page.status)}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                      Patient URN: {page.patient.nhsNumber}
                    </h3>
                  </div>
                </div>

                {/* Badges Row */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(page.priority)}`}>
                    <span className="hidden sm:inline">Priority {page.priority} - {getPriorityLabel(page.priority)}</span>
                    <span className="sm:hidden">P{page.priority}</span>
                  </span>
                  {page.reviewed && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                      <CheckCircle2 className="w-3 h-3" />
                      Reviewed
                    </span>
                  )}
                </div>

                {/* Location */}
                <p className="text-xs sm:text-sm text-gray-600 mb-2">
                  <span className="font-medium">Location:</span> {page.patient.location}
                </p>

                {/* Reason */}
                <p className="text-sm sm:text-base text-gray-700 mb-3 line-clamp-2">
                  {page.reason}
                </p>

                {page.escalationReason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                    <p className="text-sm font-medium text-red-800">
                      ⚠️ AUTO-ESCALATED: {page.escalationReason}
                    </p>
                  </div>
                )}

                {/* Footer */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-xs sm:text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {page.createdAt ? formatDistanceToNow(new Date(page.createdAt), { addSuffix: true }) : 'just now'}
                  </span>
                  <span className="capitalize font-medium">{page.status.replace('_', ' ')}</span>
                </div>

                {page.responses && page.responses.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs sm:text-sm text-gray-700">
                      <span className="font-medium">Latest response:</span> {page.responses[page.responses.length - 1].message}
                    </p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default NurseDashboard;
